import React, { useState } from "react";

import { CalculatorKeypad } from "../../../components/shared/CalculatorKeypad";
import WorkspaceCalendarGrid from "./WorkspaceCalendarGrid";
import { WORKSPACE_TOOL } from "./WorkspaceToolRail";
import LeadInspectorPanel from "./panels/LeadInspectorPanel";
import DiscoveryPanel from "./panels/DiscoveryPanel";
import QuotePanel from "./panels/QuotePanel";
import NotesPanel from "./panels/NotesPanel";
import OriginalCalendarToolPanel from "./panels/CalendarToolPanel";
import { TOOL_DEFERRED_REASON, TOOL_READ_ONLY_NOTE } from "../workspaceFrameCopy";
import { authorizedByServer } from "./panels/toolProjectionGuard";

/**
 * What each Agent Tool renders in Production.
 *
 * AW-ORIGINAL-RESTORE-002 MOUNTED THE ORIGINAL PANELS
 * ---------------------------------------------------
 * AW-ORIGINAL-RESTORE-001 made the five original panels fixture-free;
 * this file now renders them, replacing the bespoke `DeferredToolPanel`
 * paragraph that stood in for them. The agent sees the original
 * components' own governed presentation rather than an approximation of
 * it, and there is one panel implementation instead of two.
 *
 * WHAT THEY RENDER IS "UNAVAILABLE", AND THAT IS THE WHOLE POINT
 * --------------------------------------------------------------
 * Phase A established that **no governed projection envelope exists for
 * any of the five data tools**. The only envelope producers in the
 * repository -- `fakeRuntime`, `workspaceStateStore`,
 * `workspaceViewModel`, `authorizationComposition` -- are the fixture
 * tier, and nothing in Production composes one.
 *
 * So no customer data may be rendered, and none is. Each panel is
 * mounted with NO `projectionStatus` and NO data props at all, which
 * their own guard resolves to `PROJECTION_ABSENT` -- status `missing` --
 * and each renders its original `ToolProjectionNotice`.
 *
 * THIS FILE CONSTRUCTS NO PROJECTION STATUS, DELIBERATELY
 * -------------------------------------------------------
 * Not even a withheld one. Passing nothing is stronger than passing
 * `{status: "missing"}`: it means Production states nothing about
 * authorization, so there is no object here that a later edit could
 * flip to `"ok"` and thereby bypass the governed resolver. A source
 * guard asserts that no Production module contains a literal `ok`
 * status, and that guard has nothing to make an exception for.
 *
 * When a real envelope exists, the mounting change is to pass
 * `resolveWorkspaceToolProjection(...)`'s own output for both the
 * status and the data -- never a literal, never a hand-built status.
 *
 * WHY A REASON LINE SITS BESIDE THE NOTICE
 * ----------------------------------------
 * `ToolProjectionNotice` says what the *status* means ("No information
 * is currently available for this tool"), which is correct and generic.
 * The line beneath says what is missing for THAT tool -- six of the
 * seven are blocked on the same absent lead identity, and the seventh
 * never existed. Together the agent learns both the governed state and
 * the specific reason, which neither alone conveys.
 *
 * TWO TOOLS NEED NO CONTRACT AND ARE REAL
 * ---------------------------------------
 * `CalculatorKeypad` computes and stores nothing. The Calendar month
 * grid is a month, its days and a selected date. Both are mounted
 * whole; only the Calendar's appointment schedule is withheld, and it
 * is withheld through the original panel's own notice rather than a
 * sentence invented here.
 */

export function CalculatorToolPanel() {
  return (
    <CalculatorKeypad
      density="compact"
      testId="workspace-calculator-panel"
      displayTestId="workspace-calculator-display"
      gridTestId="workspace-calculator-grid"
      buttonTestIdPrefix="workspace-calc"
      showHistory
      historyTestId="workspace-calculator-history"
    />
  );
}

/**
 * The Calendar tool: the original grid, plus the original panel's own
 * governed notice in place of the schedule.
 *
 * The grid is mounted directly rather than through
 * `OriginalCalendarToolPanel`, because that component withholds its
 * ENTIRE render -- grid included -- when its projection is not `ok`,
 * and the grid needs no projection. Rendering the original panel
 * alongside it with no props yields exactly the schedule half's
 * governed `missing` notice, so both halves come from the original
 * component tree and neither invents anything.
 *
 * `selectedDate` is local, non-persistent presentation state, as in the
 * original. Nothing is fetched, stored or sent.
 */
export function CalendarToolPanel() {
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  return (
    <div className="flex flex-col gap-3" data-testid="workspace-calendar-panel">
      <WorkspaceCalendarGrid selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      <OriginalCalendarToolPanel />
      <ToolReason
        toolKey={WORKSPACE_TOOL.CALENDAR}
        testId="workspace-calendar-appointments-unavailable"
      />
    </div>
  );
}

/**
 * `testId` exists so Calendar keeps the selector AW-TOOLS-003
 * established for this line. Renaming it would cost a real assertion to
 * prove nothing.
 */
function ToolReason({ toolKey, testId }) {
  return (
    <p
      className="text-xs leading-relaxed text-slate-500"
      data-testid={testId || `workspace-tool-deferred-${toolKey}`}
    >
      {TOOL_DEFERRED_REASON[toolKey]}
    </p>
  );
}

/**
 * An original panel mounted with no projection and no data.
 *
 * The absence is the contract: the panel's own guard resolves it to
 * `missing` and renders the original `ToolProjectionNotice`. No
 * customer data can appear because none was passed, and no status was
 * asserted because none was constructed.
 */
function GovernedUnavailablePanel({ toolKey, Panel }) {
  return (
    <div className="flex flex-col gap-3" data-testid={`workspace-tool-unavailable-${toolKey}`}>
      <Panel />
      <ToolReason toolKey={toolKey} />
    </div>
  );
}

/**
 * AW-LDQ-003 -- a data-backed tool: the ORIGINAL panel, governed data,
 * and no way to edit it.
 *
 * READ-ONLY WITHOUT TOUCHING THE PANEL
 * ------------------------------------
 * The original panels are editable by design and this phase must not
 * be. A `readOnly` prop would have meant changing five components to
 * thread a flag none of them had; a `<fieldset disabled>` achieves the
 * same thing from outside, through the platform rather than around it.
 *
 * HTML disables every nested form control inside a disabled fieldset --
 * inputs, textareas, selects and buttons alike, which covers the Radix
 * primitives the panels use since each renders a real `button`. They
 * leave the tab order, they cannot receive a change event, and assistive
 * technology is told. No panel source changed, and nothing relies on
 * CSS to be true.
 *
 * `aria-readonly` says WHY to anyone who asks, and the note beneath says
 * it in words: edits happen in the Script, which is still the only
 * writer.
 */
function GovernedDataPanel({ toolKey, Panel, loading, projectionStatus, data }) {
  if (loading) {
    return (
      <p className="text-xs text-slate-500" data-testid={`workspace-tool-loading-${toolKey}`}>
        Loading…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid={`workspace-tool-data-${toolKey}`}>
      <fieldset
        disabled
        aria-readonly="true"
        className="m-0 min-w-0 border-0 p-0"
        data-testid={`workspace-tool-readonly-${toolKey}`}
      >
        <Panel {...(data || {})} projectionStatus={projectionStatus} />
      </fieldset>
      <p
        className="text-xs leading-relaxed text-slate-500"
        data-testid={`workspace-tool-readonly-note-${toolKey}`}
      >
        {TOOL_READ_ONLY_NOTE}
      </p>
    </div>
  );
}

/**
 * AW-2C-005 -- the Lead tool: the original panel, provider-backed, and
 * deliberately NOT inside a disabled fieldset.
 *
 * WHY NO FIELDSET HERE
 * --------------------
 * `GovernedDataPanel` wraps Discovery and Quote in
 * `<fieldset disabled>` because those panels are editable by design and
 * this phase must not be. The Lead inspector has no editable control at
 * all -- its only interactions are the per-field copy buttons and the
 * comments Show more/less toggle, both `<button>` elements. A disabled
 * fieldset disables every nested button, so applying the same wrapper
 * here would make copy-to-clipboard inert while still looking present:
 * #685's defect exactly, on a control an agent uses to paste a
 * customer's details into a carrier application mid-call.
 *
 * So read-only is a property of the panel rather than of a wrapper, and
 * there is no note beneath it: there is nothing to explain, because
 * nothing was taken away.
 *
 * WHAT GOVERNS IT INSTEAD
 * -----------------------
 * The same thing that governs the other two -- the panel's own
 * `resolveToolRenderStatus` guard, which renders data only on an `ok`
 * status carrying all three required sections. The status comes from
 * `authorizedByServer()`, whose precondition is that a run-scoped or
 * call-scoped endpoint returned 2xx, and for this tool that endpoint
 * revalidated ownership after its own provider read (AW-2C-004).
 */
function GovernedLeadPanel({ customer, commentsTruncated }) {
  return (
    <div className="flex flex-col gap-3" data-testid="workspace-tool-data-lead">
      <LeadInspectorPanel
        {...customer}
        commentsTruncated={commentsTruncated}
        projectionStatus={authorizedByServer()}
      />
    </div>
  );
}

/**
 * The Lead tool while its customer is being fetched or is unavailable.
 *
 * Loading says so plainly. Unavailable renders the ORIGINAL panel's own
 * governed notice -- by passing no projection, which its guard resolves
 * to `missing` -- rather than a sentence invented here, plus the
 * tool-specific reason line the rail has always carried.
 */
function LeadToolPlaceholder({ loading }) {
  if (loading) {
    return (
      <p className="text-xs text-slate-500" data-testid="workspace-tool-loading-lead">
        Loading…
      </p>
    );
  }
  return <GovernedUnavailablePanel toolKey={WORKSPACE_TOOL.LEAD} Panel={LeadInspectorPanel} />;
}

/** Tools with no implementation at all, in the original shell either. */
function NeverImplementedTool({ toolKey }) {
  return <ToolReason toolKey={toolKey} />;
}

/**
 * Which tools the agent may select. All eight: choosing a tool and
 * opening its panel costs nothing and claims nothing, and the original
 * shell has always allowed it.
 */
export const PRODUCTION_SELECTABLE_TOOLS = Object.freeze(
  Object.fromEntries(Object.values(WORKSPACE_TOOL).map((key) => [key, true]))
);

/** The tool the workspace opens on. Lead is the original's own default. */
export const DEFAULT_PRODUCTION_TOOL = WORKSPACE_TOOL.LEAD;

/** Original panel per tool, for the tools that have one. */
const ORIGINAL_PANEL = Object.freeze({
  [WORKSPACE_TOOL.LEAD]: LeadInspectorPanel,
  [WORKSPACE_TOOL.DISCOVERY]: DiscoveryPanel,
  [WORKSPACE_TOOL.QUOTE]: QuotePanel,
  [WORKSPACE_TOOL.NOTES]: NotesPanel,
});

/**
 * Resolves a tool key to its Production panel. Pure: same key in, same
 * element out, no state and no side effect.
 */
/** Tools whose panel is fed from the run's governed data. */
const DATA_PANEL = Object.freeze({
  [WORKSPACE_TOOL.DISCOVERY]: DiscoveryPanel,
  [WORKSPACE_TOOL.QUOTE]: QuotePanel,
});

export default function resolveToolPanel(toolKey, toolData = null, customerData = null) {
  if (toolKey === WORKSPACE_TOOL.CALCULATOR) return <CalculatorToolPanel />;
  if (toolKey === WORKSPACE_TOOL.CALENDAR) return <CalendarToolPanel />;

  // AW-2C-005. Lead is fed from the shared call-scoped customer
  // snapshot, not from a tool-specific fetch -- the same object
  // the Script runner interpolates from, so the two surfaces cannot
  // disagree about who is on the call.
  if (toolKey === WORKSPACE_TOOL.LEAD && customerData) {
    if (customerData.customer) {
      return (
        <GovernedLeadPanel
          customer={customerData.customer}
          commentsTruncated={customerData.commentsTruncated}
        />
      );
    }
    if (customerData.loading || customerData.unavailable) {
      return <LeadToolPlaceholder loading={customerData.loading} />;
    }
  }

  // AW-LDQ-003. Discovery and Quote are data-backed when a run is
  // current. With no run -- between calls -- `toolData` is null and they
  // fall through to the governed-unavailable path below, which is the
  // honest answer rather than an empty form.
  const DataPanel = DATA_PANEL[toolKey];
  if (DataPanel && toolData && (toolData.loading || toolData.projectionStatus)) {
    return (
      <GovernedDataPanel
        toolKey={toolKey}
        Panel={DataPanel}
        loading={toolData.loading}
        projectionStatus={toolData.projectionStatus}
        data={toolData.data}
      />
    );
  }

  const Panel = ORIGINAL_PANEL[toolKey];
  if (Panel) return <GovernedUnavailablePanel toolKey={toolKey} Panel={Panel} />;

  // Actions and History: a single line of placeholder text at
  // 23d500e27 too. There is no original panel to mount.
  if (TOOL_DEFERRED_REASON[toolKey]) return <NeverImplementedTool toolKey={toolKey} />;
  return null;
}
